import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { adminDeleteCoupon, adminUpsertCoupon, type CouponInput } from "@/lib/callables";
import type { Coupon } from "@/types";
import { toDate } from "@/lib/format";
import { fetchCollection } from "./use-firestore";

export function useCoupons() {
  return useQuery({
    queryKey: queryKeys.coupons(),
    queryFn: () => fetchCollection<Coupon>("coupons"),
    select: (rows) =>
      [...rows].sort((a, b) => {
        const da = toDate(a.createdAt)?.getTime() ?? 0;
        const db_ = toDate(b.createdAt)?.getTime() ?? 0;
        return db_ - da;
      }),
  });
}

export function useUpsertCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CouponInput) => adminUpsertCoupon(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.coupons() }),
  });
}

export function useDeleteCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminDeleteCoupon({ id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.coupons() }),
  });
}

/**
 * Um cupom é utilizável quando está ativo e dentro do prazo e do limite.
 * `now` em milissegundos; a tela passa o momento em que a lista chegou.
 */
export function isCouponUsable(coupon: Coupon, now: number = Date.now()): boolean {
  if (!coupon.isActive) return false;
  const expires = toDate(coupon.expirationDate);
  if (expires && expires.getTime() < now) return false;
  if (coupon.usageLimit != null && (coupon.usageCount ?? 0) >= coupon.usageLimit) return false;
  return true;
}
